import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { Descriptions, Image, Spin, Table, Typography } from 'antd';
import { getCarDetails } from '../../app/api';
import { useCarContext } from '../../context/CarContext';
import moment from 'moment';

const { Text } = Typography;

const CarDetails = () => {
    const { carId } = useParams();
    const { carDetails, setCarDetails } = useCarContext();
    const [loading, setLoading] = useState(true);

    const items = [
        {
          key: '1',
          label: 'Օգտատերի Անուն',
          children: carDetails?.username || "Նյութերը չեն գտնվել",
        },
        {
          key: '2',
          label: 'Օգտատերի Հեռախոսահամարը',
          children: carDetails?.user_mobile_number || "Մուտք չի կատարվել",
        },
        {
          key: '3',
          label: 'Հասցեն',
          children: carDetails?.location || "Անհայտ",
        },
        {
          key: '4',
          label: 'Տեսակը',
          children: carDetails?.make || "Անհայտ",
        },
        {
          key: '5',
          label: 'Մոդելը',
          children: carDetails?.model || "Անհայտ",
        },
        {
          key: '6',
          label: 'Մեքենայի համարանիշ',
          children: carDetails?.car_number || "Անհայտ",
        },
        {
          key: '7',
          label: 'VIN համարը',
          span: 2,
          children: carDetails?.win_number 
            ? <Text copyable>{carDetails.win_number}</Text> 
            : "Անհայտ",
        },
    ];

    const serviceTypeMap = {
      engine_oil: "Շարժիչի յուղի փոխարինում",
      transmission: "Փոխանցման տուփի փոխարինում",
      hydraulic_or_differential: "Հիդրավլիկ կամ մոստ",
    };

    const columns = [
      {
        title: '№',
        dataIndex: 'index',
        key: 'index',
        render: (text, record, index) => index + 1,
      },
      {
        title: 'Ավտոմեքենայի Համարանիշ',
        dataIndex: 'car_number',
        key: 'car_number',
        render: () => carDetails?.car_number || "Անհայտ",
      },
      {
        title: 'Յուղի Անուն',
        dataIndex: 'oil_name',
        key: 'oil_name',
      },
      {
        title: 'Յուղի Տեսակ',
        dataIndex: 'oil_type',
        key: 'oil_type',
      },
      {
        title: 'Վազք',
        dataIndex: 'run',
        key: 'run',
        render: (text, record) => `${text} ${record.runUnit}`,
      },
      {
        title: 'Հաջորդ Վազք',
        dataIndex: 'nextRun',
        key: 'nextRun',
        render: (text, record) => `${text} ${record.runUnit}`,
      },
      {
        title: 'Սպասարկման Տեսակ',
        dataIndex: 'serviceType',
        key: 'serviceType',
        render: (text) => serviceTypeMap[text] || "Անհայտ",
      },
      {
        title: 'Ֆիլտրեր',
        dataIndex: 'filters',
        key: 'filters',
        render: (_, record) => {
          const filters = [];
          if (record.oilFilter) filters.push("Յուղի");
          if (record.cabinFilter) filters.push("Սրահի");
          if (record.airFilter) filters.push("Օդի");
      
          return filters.length > 0 ? filters.join(", ") : "Չկա";
        },
      },
      {
        title: 'Փոխման Ամսաթիվ',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (text) => {
          const formattedDate = moment(text).format('ll');
          const fromNow = moment(text).fromNow();
          return `${formattedDate} (${fromNow})`;
        }
      },
    ]; 

    const task_columns = [
      {
        title: '№',
        dataIndex: 'index',
        key: 'index',
        render: (text, record, index) => index + 1,
      },
      {
        title: 'Անվանում',
        dataIndex: 'work_name',
        key: 'work_name',
        render: (workNames) => (
          <>
            {workNames?.filter(name => name.trim() !== "").map((name, index) => (
              <div key={index}>{name},</div>
            ))}
          </>
        ),
        onCell: () => ({
          style: { 
            maxWidth: 300, 
            wordWrap: 'break-word', 
            whiteSpace: 'normal' 
          }
        })    
      },
      {
        title: 'Կատարված Աշխատանքը',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Գումարը',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
      },
      {
        title: 'Փոխման Ամսաթիվ',
        dataIndex: 'date',
        key: 'date',
        render: (text) => {
          const formattedDate = moment(text).format('ll');
          return formattedDate;
        }
      },
    ]; 

    const fetchCarDetails = async () => {
      setLoading(true);
      try {
          const allData = await getCarDetails(carId);
          if (allData.status === 200) {
              setCarDetails(allData.data);
          }
      } catch (error) {
          console.error("Failed to fetch car details", error);
      } finally {
          setLoading(false);
      }
    }

    useEffect(() => {
        fetchCarDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
      return (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
              <Spin size="large" />
              <p>Տվյալները բեռնվում են...</p>
          </div>
      );
    }
    
    return (
      <div>
        <Image
          width={300}
          max-height={200}
          src={carDetails?.imgURL || null}
          fallback="data:image/png;base64,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"
        />
        <Descriptions style={{marginTop: "20px"}} title="Մեքենաների Տվյալները" layout="vertical" items={items} />
        <div style={{ padding: '15px' }} >
          <h3 style={{ marginBottom: '15px' }}>Մեքենայի սպասարկման տվյալներ</h3>
          <div 
            style={{
              border: '1px solid #d9d9d9',
              padding: '15px'
            }}
          >
            <h4 style={{ marginBottom: '15px' }}>Յուղի փոխարինում</h4>
            {carDetails?.oils.length > 0 ? <Table
                columns={columns}
                dataSource={carDetails.oils}
                style={{marginTop: "20px"}}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={false}
            /> : null}
          </div>
          <div 
            style={{
              border: '1px solid #d9d9d9',
              padding: '15px',
              marginTop: "20px"
            }}
          >
            <h4 style={{ marginBottom: '15px' }}>Կտարված աշխատանքներ</h4>
            {carDetails?.CompletedTasks.length > 0 ? <Table
                columns={task_columns}
                dataSource={carDetails.CompletedTasks}
                style={{marginTop: "20px"}}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={false}
                onRow={(record) => ({
                    onClick: () => window.location.href = `/done/${record.id}`,
                    style: { cursor: 'pointer' },
                })}
            /> : null}
          </div>
        </div>
      </div>
    )
};

export default CarDetails;
